import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";

const backgroundImages = [
  "/renamed_website_images/physics-lab-1.jpg",
  "/renamed_website_images/physics-lab-2.jpg",
  "/renamed_website_images/physics-lab-3.jpg",
];

const experiments = [
  {
    img: "/renamed_website_images/physics-lab-4.jpg",
    title: "Simple Pendulum & Oscillations",
    desc: "Precision timing and harmonic motion studies calculating acceleration due to gravity.",
    accent: "#6366f1",
  },
  {
    img: "/renamed_website_images/physics-lab-2.jpg",
    title: "Light & Optics",
    desc: "Ray optics benches studying reflection, refraction, lenses, prisms, and focal lengths.",
    accent: "#22c55e",
  },
  {
    img: "/renamed_website_images/physics-lab-3.jpg",
    title: "Magnetism & Field Lines",
    desc: "Hands-on bar magnets, compass needles, and electromagnets exploring magnetic force.",
    accent: "#f4c842",
  },
  {
    img: "/renamed_website_images/physics-lab-1.jpg",
    title: "Electric Circuits & Resistance",
    desc: "Ohm's law apparatus, rheostats, voltmeters, and ammeters for circuit experiments.",
    accent: "#f97316",
  },
];

const PhysicsLab = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImageIndex(
        (prevIndex) => (prevIndex + 1) % backgroundImages.length,
      );
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section className="relative min-h-screen py-24 px-6 md:px-16 pt-36 md:pt-40 overflow-hidden bg-[#020810]">
      {/* Smooth Cross-fade Background */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        {backgroundImages.map((img, idx) => (
          <div
            key={idx}
            className="absolute inset-0 bg-cover bg-center transition-opacity duration-1000 ease-in-out"
            style={{
              backgroundImage: `url('${img}')`,
              opacity: currentImageIndex === idx ? 1 : 0,
              transition: "opacity 1.5s ease-in-out",
            }}
          />
        ))}
      </div>

      {/* Dark overlay */}
      <div
        className="absolute inset-0 z-1"
        style={{ background: "rgba(2,8,16,0.88)" }}
      />

      <div className="max-w-6xl mx-auto relative z-10">
        <Link
          to="/facilities"
          className="inline-block text-sm font-bold tracking-wide mb-8 transition-colors duration-200"
          style={{ fontFamily: "Nunito, sans-serif", color: "#f4c842" }}
        >
          ← Back to Facilities
        </Link>

        <h1
          className="uppercase mb-4"
          style={{
            fontFamily: "Orbitron, sans-serif",
            fontWeight: 900,
            fontSize: "clamp(1.8rem, 4vw, 3rem)",
            color: "#f0f5ff",
          }}
        >
          Physics Lab
        </h1>
        <div className="cosmic-divider mb-10" />

        {/* Experiment cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
          {experiments.map((exp, i) => (
            <div
              key={i}
              className="glass-card overflow-hidden transition-all duration-300 hover:scale-[1.02] flex flex-col"
              style={{ borderColor: `${exp.accent}33` }}
            >
              <div className="h-48 overflow-hidden">
                <img
                  src={exp.img}
                  alt={exp.title}
                  className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                />
              </div>
              <div className="p-5 flex-1 flex flex-col justify-between">
                <div>
                  <h4
                    className="font-bold mb-2"
                    style={{
                      fontFamily: "Cinzel, serif",
                      color: exp.accent,
                      fontSize: "0.95rem",
                    }}
                  >
                    {exp.title}
                  </h4>
                  <p className="text-xs text-gray-400 leading-relaxed">
                    {exp.desc}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Description */}
        <div
          className="glass-card p-8 mb-12"
          style={{ borderColor: "rgba(99,102,241,0.2)" }}
        >
          <p
            className="leading-relaxed text-center text-gray-300 text-sm md:text-base max-w-4xl mx-auto"
            style={{
              fontFamily: "Nunito, sans-serif",
            }}
          >
            The Physics Lab is designed to nurture scientific thinking and
            hands-on learning. Equipped with precision apparatus, students perform
            experiments aligned with the ICSE curriculum — exploring gravity,
            optics, magnetism, and circuits to develop a thorough practical understanding.
          </p>
        </div>

        {/* Bottom Navigation Buttons */}
        <div className="flex items-center justify-between pt-6 border-t border-white/10">
          <Link
            to="/facilities/chemistry-lab"
            className="flex items-center gap-2 px-5 py-2.5 rounded-full glass-card text-xs uppercase tracking-widest text-gold hover:border-gold transition-all"
          >
            <FaArrowLeft /> Previous: Chemistry Lab
          </Link>
          <Link
            to="/facilities/computer-lab"
            className="flex items-center gap-2 px-5 py-2.5 rounded-full cosmic-btn-gold text-xs uppercase tracking-widest"
          >
            Next: Computer Lab <FaArrowRight />
          </Link>
        </div>
      </div>

      {/* Side floating arrows */}
      <Link
        to="/facilities/chemistry-lab"
        aria-label="Previous Lab: Chemistry Lab"
        className="fixed z-50 left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full items-center justify-center flex transition-all duration-300 shadow-xl"
        style={{
          background: "rgba(2,8,16,0.85)",
          border: "1px solid rgba(244,200,66,0.3)",
          color: "#f4c842",
          backdropFilter: "blur(8px)",
        }}
      >
        <FaArrowLeft />
      </Link>
      <Link
        to="/facilities/computer-lab"
        aria-label="Next Lab: Computer Lab"
        className="fixed z-50 right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full items-center justify-center flex transition-all duration-300 shadow-xl"
        style={{
          background: "rgba(2,8,16,0.85)",
          border: "1px solid rgba(244,200,66,0.3)",
          color: "#f4c842",
          backdropFilter: "blur(8px)",
        }}
      >
        <FaArrowRight />
      </Link>
    </section>
  );
};

export default PhysicsLab;
